import { useParams } from 'react-router-dom';

import MasterDetail from '@frolight/components/MasterDetail';
import PageLayout from '@frolight/components/PageLayout';
import Panel from '@frolight/components/Panel';

import useFormattedLocalizedText from '@frolight/hooks/useLocalizedFormattedText';
import usePageTitle from '@frolight/hooks/usePageTitle';

import useConnectedUnitsData from './hooks/useConnectedUnitsData';
import useGatewayData from './hooks/useGatewayData';
import useOrganisationGatewayData from './hooks/useOrganisationGatewayData';

import GatewayDetailConnectedUnitDetails from './components/GatewayDetailConnectedUnitDetails';

const GatewayDetail = () => {
  const { lftext } = useFormattedLocalizedText();

  const { gatewayId } = useParams();

  const { loading: gatewayDataLoading, data: gatewayData, error: gatewayDateError } = useGatewayData(gatewayId);
  const { loading: connectedUnitsDataLoading, data: connectedUnitsData, error: connectedUnitsDataError } = useConnectedUnitsData(gatewayData?.id);
  const { loading: gatewayOrganisationDataLoading, data: gatewayOrganisationData, error: gatewayOrganisationDataError } = useOrganisationGatewayData(gatewayData);
  
  usePageTitle(gatewayData ? `Gateway ${gatewayData.name} details` : `Gateway details`);

  const renderGatewayConnectionStatusBadge = (connected) => {
    if (connected) {
      return (
        <span
          className="text_with_icon  text_with_icon--icon_prepended  text_with_icon--success"
          data-icon="cloud"
        >
          {lftext('pages.device_detail.connected')}
        </span>
      );
    } else {
      return (
        <span
          className="text_with_icon  text_with_icon--icon_prepended  text_with_icon--error"
          data-icon="cloud_off"
        >
          {lftext('pages.device_detail.not_connected')}
        </span>
      );
    }
  };

  const renderOrganisationBadge = (loading, error, data = null) => {
    if (loading) {
      return <>{lftext('common.loading')}</>;
    } else if (error) {
      return <>{lftext('pages.device_detail.error_getting_organisation')}</>;
    } else if (data) {
      return <>{ data.name }</>;
    } else {
      return <>{lftext('pages.device_detail.not_registered')}</>;
    }
  }

  return (
    <PageLayout>
      <h1 className="descriptive_page_title">{lftext('pages.device_detail.title')}</h1>
      {
        gatewayDataLoading && (
          <Panel hasPadding={false} size="huge">
            <div className="loader">
              <p>{lftext('common.loading')}</p>
            </div>
          </Panel>
        )
      }
      {
        !gatewayDataLoading && gatewayData && (
          <div className="card_list_container">
            <div className="card_list_container__item  card_list_container__item--xl">
              <article className="card  card--horizontal">
                <div className="card__content">
                  <div className="card__header">
                    <hgroup className="card__identification">
                      <h2 className="card__title">{ gatewayData.name }</h2>
                      <p className="card__subtitle">
                      { renderGatewayConnectionStatusBadge(gatewayData.connected) } &middot; { renderOrganisationBadge(gatewayOrganisationDataLoading, gatewayOrganisationDataError, gatewayOrganisationData) } &middot; { gatewayData.description }
                      </p>
                    </hgroup>
                  </div>
                </div>
              </article>
            </div>
            {
              connectedUnitsDataLoading ? (
                <Panel
                  size="huge"
                  hasPadding={ false }
                >
                  <div className="loader">
                    <p>{lftext('common.loading')}</p>
                  </div>
                </Panel>
              ) : (
                connectedUnitsData && connectedUnitsData.length ? (
                  <Panel
                    size="huge"
                    hasPadding={ false }
                  >
                    <MasterDetail
                      itemsTitle={lftext('pages.device_detail.master_detail.title')}
                      items={
                        connectedUnitsData.reduce((renderedUnits, unit) => {
                          // Filter out generator units for now
                          if (unit.type === 'generator') {
                            return renderedUnits;
                          }

                          const newUnitTypeLabel = 
                            unit.type === 'master'
                              ? lftext('common.types.master')
                              : unit.type === 'slave'
                                ? lftext('common.types.slave')
                                : unit.type;
                          
                          return [...renderedUnits, {
                            label: unit.name || 'n/a',
                            icon: unit.isActive ? {
                              label: lftext('common.status.active'),
                              name: 'check_circle',
                              status: 'success'
                            } : {
                              label: lftext('common.status.inactive'),
                              name: 'offline_bolt',
                              status: 'disabled'
                            },
                            typeLabel: newUnitTypeLabel,
                            metadata: newUnitTypeLabel,
                            content: <GatewayDetailConnectedUnitDetails gateway={ gatewayData } unit={ unit } />
                          }]
                        }, [])
                      }
                    />
                  </Panel>
                ) : (
                  <Panel
                    size="huge"
                    title={lftext('pages.device_detail.panel_title')}
                  >
                    <p>{lftext('pages.device_detail.no_units_text')}</p>
                  </Panel>
                )
              )
            }
          </div>
        )
      }
    </PageLayout>
  );
}

export default GatewayDetail;
